import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../modal/Modal";
import { CloseButton } from "../input/CloseButton";
import { TextInputField } from "../input/TextInputField";
import { useForm } from "react-hook-form";
import { Button } from "../input/Button";
import { Column } from "../layout/Column";
import { IconButton } from "../input/IconButton";
import { ReactComponent as AttachIcon } from "../icons/Attach.svg";
import { ReactComponent as CaretDownIcon } from "../icons/CaretDown.svg";
import { ReactComponent as CaretUpIcon } from "../icons/CaretUp.svg";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import ThumbnailRenderer from "../../utils/thumbnail-renderer";
import { generateImageFileThumbnail, generateVideoFileThumbnail } from "../../utils/thumbnails";
import { generatePDFThumbnail } from "../../utils/pdf-render";
import { MediaLoader } from "./MediaLoader";

import styles from "./ObjectUrlModal.scss";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";
import { ToggleInput } from "../input/ToggleInput2";

import * as pdfjs from "pdfjs-dist";

export function ObjectUrlMediaModal({ onSubmit, onClose, entry }) {
  pdfjs.GlobalWorkerOptions.workerSrc =
    require("!!file-loader?outputPath=assets/js&name=[name]-[hash].js!pdfjs-dist/build/pdf.worker.min.js").default;

  const { handleSubmit, register, watch, setValue } = useForm();

  const thumbnailRenderer = new ThumbnailRenderer();

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [fileNodeThumbnail, setThumbnail ] = useState(null);
  const [processingThumbnail, setProcessingThumbnail] = useState(false);
  const [notAllowedFile, setNotAllowedFile] = useState(false);

  const processThumbnail = async () => {
    let blob
    if (entry?.type === "model" || entry?.name?.toLowerCase().endsWith('.glb')) {
      const loader = new GLTFLoader();
      const url = URL.createObjectURL(entry);

      const {scene} = await loader.loadAsync(url);

      URL.revokeObjectURL(url);

      blob = await thumbnailRenderer.generateThumbnail(
        scene,
      );
    } else if (
      entry?.type === "image" || ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) =>
        entry?.name?.toLowerCase().endsWith(ext)
      )
    ) {
      blob = entry;

    } else if (entry?.name?.toLowerCase().endsWith('.mp4')) {
      blob = entry
    } else if (entry?.name?.toLowerCase().endsWith('.pdf') || 
      entry?.url?.toLowerCase().endsWith('.pdf')) {
      blob = await generatePDFThumbnail(entry);
    } else {
      setNotAllowedFile(true);
    }
    let urlFile = URL.createObjectURL(blob);
    setThumbnail(urlFile);
    setProcessingThumbnail(false);
  }

  useEffect(() => {
    if(entry.url === undefined || entry.url.toLowerCase().endsWith('.pdf')) {
      setProcessingThumbnail(true);
      processThumbnail();
    }

    register("url");
    register("href");
    register("hideHref");
    register("smoothTransition");
  }, [register]);

  const file = watch("file");
  const hasFile = file && file.length > 0;
  const fileName = hasFile ? file[0].name : undefined;

  const onClear = useCallback(() => {
    if (hasFile) {
      setValue("file", undefined);
    } else {
      setValue("url", "");
    }
  }, [hasFile, setValue]);

  const onChange = useCallback(
    (e, field, type = "text") => {
      if (field === "url" && hasFile) {
        return;
      }

      setValue(field, type === "toggle" ? e.target.checked : e.target.value);
    },
    [hasFile, setValue]
  );

  const url = watch("url", "");
  const href = watch("href", "");
  const hideHref = watch("hideHref", false);
  const smoothTransition = watch("smoothTransition", false);

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  const showCloseButton = hasFile || url.length > 0;

  const listedMediaNode = (<>
    <img src={entry?.public_thumbnail || entry?.thumbnail}></img>
  </>)

  const fileNode = fileNodeThumbnail 
      ? entry?.name?.toLowerCase().endsWith('.mp4')
        ? <video controls src={fileNodeThumbnail}></video>
        : <img src={fileNodeThumbnail}></img>
      : <MediaLoader inlineLoading={true}  />;

  return (
    <Modal
      afterTitle={<CloseButton onClick={onClose} className={'close-dialog'} />}
      titleNode={
          notAllowedFile 
            ? <h5 className="not-allowed-file">Preview not allowed for this extension of file</h5>
            :(fileNodeThumbnail || processingThumbnail) 
              ? fileNode 
              : listedMediaNode}
      headerClassName={styles.header}
      afterTitleClassName={styles.afterTitle}
      className={styles.objectMediaModal}
      contentClassName={styles.modalContent}
    >
      <Column as="form" padding center onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.advancedOptions} onClick={toggleAdvancedOptions}>
            <h3>
              <FormattedMessage id="room-settings-sidebar.advanced-options" defaultMessage="Advanced options" />
              
            </h3>
            {showAdvancedOptions ? <CaretUpIcon /> : <CaretDownIcon />}
            
        </div>
        {showAdvancedOptions && (<>
          <p className={styles.attachUrl}>Attach custom external URL</p>
          <TextInputField
            className="text-input-field"
            name="href"
            placeholder="Add Url..."
            type={hasFile ? "text" : "url"}
            value={href}
            onChange={e => onChange(e, "href")}
            description={
              <span style={{textAlign:'center'}}>
                <FormattedMessage id="object-url-modal.href-field-description" defaultMessage="Specify a custom URL for this object to redirect to an external site." />
              </span>
            }
          />

        
          <div className="advanced-options-content">
            <ToggleInput
              className={styles.hideHrefToggleMediaModal}
              name="hideHref"
              label={
                <FormattedMessage
                  id="object-url-modal.hide-href-field-label"
                  defaultMessage="Hide 'Open Link' button"
                />
              }
              description={
                <FormattedMessage
                  id="object-url-modal.hide-href-field-description"
                  defaultMessage="Hide 'Open Link' button for the Custom URL and make the object fully clickable"
                />
              }
              checked={hideHref}
              onChange={e => onChange(e, "hideHref", "toggle")}
            />
            <ToggleInput
              className={styles.hideHrefToggleMediaModal}
              name="smoothTransition"
              label={
                <FormattedMessage
                  id="object-url-modal.smooth-transition-field-label"
                  defaultMessage="Portal Smooth Transition"
                />
              }
              description={
                <FormattedMessage
                  id="object-url-modal.smooth-transition-field-description"
                  defaultMessage="Enable smooth transition when entering or exiting a portal, only takes effect when the object is a portal"
                />
              }
              checked={smoothTransition}
              onChange={e => onChange(e, "smoothTransition", "toggle")}
            />
          </div>

        </>)}
        <Button type="submit" preset="primary" className={styles.mediaModalButton} >
          Place
        </Button>
      </Column>
    </Modal>
  );
}
